// src/pages/Appli/AppliAdmin/AboutAdmin/AboutAdmin.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import DOMPurify from "dompurify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  getAboutInfo,
  updateAboutInfo,
  uploadParcoursImage,
  uploadProjetImage,
} from "./../../../../services/aboutServices";

import { useNotification } from "./../../../../components/Notification/NotificationManager";
import { ToastContainer, toast } from "react-toastify";

const LANGUAGES = ["fr", "en"];
const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const toAbs = (url) => {
  if (!url) return "";
  return isExternalURL(url) ? url : `${BASE_URL}${url}`;
};

const sanitize = (html) => DOMPurify.sanitize(String(html || ""));

/**
 * ✅ IMPORTANT : composant DÉCLARÉ AU NIVEAU DU MODULE
 * => stable entre les renders
 * => les inputs ne perdent plus le focus
 */
const SectionCard = React.memo(function SectionCard({
  sectionKey,
  label,
  imageKey,
  titleKeyBase,
  textKeyBase,
  editing,
  currentLang,
  formData,
  aboutData,
  ui,
  uploading,
  startEditing,
  stopEditing,
  handleSave,
  handleChange,
  handleImageUpload,
  setLightboxImg,
}) {
  const titleValue =
    formData?.[sectionKey]?.[`${titleKeyBase}_${currentLang}`] ??
    aboutData?.[`${titleKeyBase}_${currentLang}`] ??
    "";

  const textValue =
    formData?.[sectionKey]?.[`${textKeyBase}_${currentLang}`] ??
    aboutData?.[`${textKeyBase}_${currentLang}`] ??
    "";

  const imageValue =
    formData?.[sectionKey]?.[imageKey] ?? aboutData?.[imageKey] ?? "";

  const imageUrl = toAbs(imageValue);

  return (
    <section className={`${ui.card} overflow-hidden`}>
      {/* Header */}
      <div className="px-5 sm:px-6 py-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-xs font-semibold ${ui.muted}`}>
            {label}{" "}
            <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20 text-gray-900 dark:text-white">
              {currentLang.toUpperCase()}
            </span>
          </div>

          <h2 className={`mt-1 text-xl sm:text-2xl font-extrabold ${ui.title}`}>
            {titleValue || "Titre non disponible"}
          </h2>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {editing ? (
            <>
              <button
                type="button"
                className={ui.btnGhost}
                onClick={() => stopEditing(sectionKey)}
              >
                Annuler
              </button>
              <button
                type="button"
                className={ui.btnPrimary}
                onClick={() => handleSave(sectionKey)}
              >
                Enregistrer
              </button>
            </>
          ) : (
            <button
              type="button"
              className={ui.btnPrimary}
              onClick={() => startEditing(sectionKey)}
            >
              Modifier
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Image */}
        <div className="lg:col-span-5">
          <div className="group relative overflow-hidden rounded-3xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20">
            <div className="aspect-[16/10] w-full flex items-center justify-center">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={label}
                  className="h-full w-full object-contain"
                  loading="lazy"
                />
              ) : (
                <div className={`text-sm ${ui.muted} p-6 text-center`}>
                  Aucune image
                </div>
              )}
            </div>

            {/* Hover actions */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition bg-black/20 flex items-end justify-between p-3">
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-bold bg-white/80 text-gray-900"
                onClick={() => imageUrl && setLightboxImg(imageUrl)}
                disabled={!imageUrl}
              >
                Agrandir
              </button>

              {editing && (
                <label className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-bold bg-primary text-white cursor-pointer">
                  {uploading?.[sectionKey] ? "Upload…" : "Changer"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) =>
                      handleImageUpload(sectionKey, e.target.files?.[0])
                    }
                    disabled={uploading?.[sectionKey]}
                  />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Texte */}
        <div className="lg:col-span-7">
          {editing ? (
            <>
              <div className="mb-3">
                <label className={`text-xs font-semibold ${ui.muted} mb-1 block`}>
                  Titre
                </label>
                <input
                  className={ui.input}
                  value={
                    formData?.[sectionKey]?.[`${titleKeyBase}_${currentLang}`] ||
                    ""
                  }
                  onChange={(e) =>
                    handleChange(sectionKey, titleKeyBase, e.target.value)
                  }
                  placeholder="Titre…"
                />
              </div>

              <div className="mb-3">
                <label className={`text-xs font-semibold ${ui.muted} mb-1 block`}>
                  Contenu
                </label>

                <div className="rounded-3xl border border-black/10 dark:border-white/10 overflow-hidden bg-white/70 dark:bg-black/20">
                  <ReactQuill
                    value={
                      formData?.[sectionKey]?.[`${textKeyBase}_${currentLang}`] ||
                      ""
                    }
                    onChange={(val) => handleChange(sectionKey, textKeyBase, val)}
                    className="aboutadmin-quill"
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20 p-4 sm:p-5 max-h-[520px] overflow-y-auto">
              <div
                className="prose prose-neutral max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{
                  __html: sanitize(textValue || "<p>Texte non disponible</p>"),
                }}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
});

const AboutAdmin = () => {
  const { addNotification } = useNotification();

  const [aboutData, setAboutData] = useState(null);
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState({ parcours: false, projet: false });
  const [currentLang, setCurrentLang] = useState("fr");

  const [uploading, setUploading] = useState({ parcours: false, projet: false });
  const [lightboxImg, setLightboxImg] = useState(null);

  const ui = useMemo(() => {
    const page = "min-h-screen";
    const wrap = "max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8";
    const title = "text-gray-900 dark:text-white";
    const muted = "text-gray-700 dark:text-gray-300";

    const card =
      "rounded-3xl border border-black/10 bg-white/70 shadow-sm backdrop-blur-sm " +
      "dark:bg-white/5 dark:border-white/10 dark:shadow-none";

    const btnPrimary =
      "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold " +
      "bg-primary text-white shadow hover:opacity-95 active:scale-[0.99] transition";

    const btnGhost =
      "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white/70 dark:bg-black/20 " +
      "text-gray-900 dark:text-white " +
      "hover:bg-white dark:hover:bg-black/30 transition";

    const input =
      "w-full rounded-2xl px-3 py-2 text-sm outline-none transition " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white/70 dark:bg-black/20 " +
      "text-gray-900 dark:text-white " +
      "focus:ring-2 focus:ring-primary focus:border-transparent";

    return { page, wrap, title, muted, card, btnPrimary, btnGhost, input };
  }, []);

  const load = useCallback(async () => {
    try {
      const response = await getAboutInfo();
      setAboutData(response);

      setFormData({
        parcours: {
          parcours_title_fr: response.parcours_title_fr || "",
          parcours_text_fr: response.parcours_text_fr || "",
          parcours_title_en: response.parcours_title_en || "",
          parcours_text_en: response.parcours_text_en || "",
          parcours_image: response.parcours_image || "",
        },
        projet: {
          projet_title_fr: response.projet_title_fr || "",
          projet_text_fr: response.projet_text_fr || "",
          projet_title_en: response.projet_title_en || "",
          projet_text_en: response.projet_text_en || "",
          projet_image: response.projet_image || "",
        },
      });
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement des données.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = useCallback((section, baseName, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [`${baseName}_${prev.__lang || "fr"}`]: value, // fallback si besoin
      },
    }));
  }, []);

  // NOTE: pour garantir currentLang dans handleChange, on le fixe dans formData (simple et efficace)
  useEffect(() => {
    setFormData((prev) => ({ ...prev, __lang: currentLang }));
  }, [currentLang]);

  const startEditing = useCallback(
    (section) => setIsEditing((s) => ({ ...s, [section]: true })),
    []
  );
  const stopEditing = useCallback(
    (section) => setIsEditing((s) => ({ ...s, [section]: false })),
    []
  );

  const handleSave = useCallback(
    async (section) => {
      if (!aboutData?.id) return;

      try {
        const payload = { id: aboutData.id, ...formData[section] };
        await updateAboutInfo(payload);

        setAboutData((prev) => ({ ...prev, ...payload }));
        stopEditing(section);

        addNotification("Section mise à jour avec succès !", "success");
        toast.success("Modifications enregistrées ✅");
      } catch (error) {
        console.error(error);
        addNotification("Erreur lors de la mise à jour des données.", "error");
        toast.error("Erreur lors de l’enregistrement.");
      }
    },
    [aboutData?.id, formData, stopEditing, addNotification]
  );

  const handleImageUpload = useCallback(
    async (section, file) => {
      if (!file) return;

      const fd = new FormData();
      if (section === "parcours") fd.append("parcoursImage", file);
      if (section === "projet") fd.append("projetImage", file);
      fd.append("category", "about");

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Token d'authentification manquant.");
        return;
      }

      setUploading((s) => ({ ...s, [section]: true }));

      try {
        const uploadResponse =
          section === "parcours"
            ? await uploadParcoursImage(fd)
            : await uploadProjetImage(fd);

        const key = section === "parcours" ? "parcours_image" : "projet_image";
        const newUrl = uploadResponse?.[key];

        if (!newUrl) throw new Error("Upload response missing image url");

        setAboutData((prev) => ({ ...prev, [key]: newUrl }));
        setFormData((prev) => ({
          ...prev,
          [section]: { ...prev[section], [key]: newUrl },
        }));

        toast.success("Image mise à jour ✅");
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de l'upload de l'image.");
      } finally {
        setUploading((s) => ({ ...s, [section]: false }));
      }
    },
    []
  );

  if (!aboutData) {
    return (
      <div className={`${ui.page} ${ui.wrap} pt-10`}>
        <div className={`${ui.card} p-6`}>
          <p className={`text-center ${ui.muted}`}>Chargement des données…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      {/* fond léger */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent dark:from-black/70" />
      </div>

      <div className={`${ui.wrap} relative z-10 pt-10 pb-10`}>
        {/* Header */}
        <div className={`${ui.card} p-5 sm:p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className={`text-2xl sm:text-3xl font-extrabold ${ui.title}`}>
                Gestion de la page “Qui suis-je ?”
              </h1>
              <p className={`mt-1 text-sm ${ui.muted}`}>
                Modifie le contenu par langue, puis enregistre section par section.
              </p>
            </div>

            {/* Lang selector */}
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold ${ui.muted}`}>Langue</span>
              <select
                value={currentLang}
                onChange={(e) => setCurrentLang(e.target.value)}
                className={ui.input}
                style={{ width: 110 }}
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>
                    {lang.toUpperCase()}
                  </option>
                ))}
              </select>

              <button type="button" className={ui.btnGhost} onClick={load} title="Recharger">
                ⟳ Recharger
              </button>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="mt-6 space-y-6">
          <SectionCard
            sectionKey="parcours"
            label="Mon parcours"
            imageKey="parcours_image"
            titleKeyBase="parcours_title"
            textKeyBase="parcours_text"
            editing={!!isEditing.parcours}
            currentLang={currentLang}
            formData={formData}
            aboutData={aboutData}
            ui={ui}
            uploading={uploading}
            startEditing={startEditing}
            stopEditing={stopEditing}
            handleSave={handleSave}
            handleChange={(section, baseName, value) =>
              setFormData((prev) => ({
                ...prev,
                [section]: {
                  ...prev[section],
                  [`${baseName}_${currentLang}`]: value,
                },
              }))
            }
            handleImageUpload={handleImageUpload}
            setLightboxImg={setLightboxImg}
          />

          <SectionCard
            sectionKey="projet"
            label="Mon projet"
            imageKey="projet_image"
            titleKeyBase="projet_title"
            textKeyBase="projet_text"
            editing={!!isEditing.projet}
            currentLang={currentLang}
            formData={formData}
            aboutData={aboutData}
            ui={ui}
            uploading={uploading}
            startEditing={startEditing}
            stopEditing={stopEditing}
            handleSave={handleSave}
            handleChange={(section, baseName, value) =>
              setFormData((prev) => ({
                ...prev,
                [section]: {
                  ...prev[section],
                  [`${baseName}_${currentLang}`]: value,
                },
              }))
            }
            handleImageUpload={handleImageUpload}
            setLightboxImg={setLightboxImg}
          />
        </div>

        <ToastContainer position="bottom-right" />
      </div>

      {/* Lightbox image */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
        >
          <img
            src={lightboxImg}
            alt="Agrandissement"
            className="max-h-[90vh] max-w-[92vw] rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default AboutAdmin;
